import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Drawer } from "@/components/ui-kit";
import { Trophy, TrendingDown, AlertTriangle, Gauge, MessageSquarePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const RANGES = [7, 30, 90];

function scoreColor(s) {
  if (s >= 90) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (s >= 75) return "text-sky-700 bg-sky-50 border-sky-200";
  if (s >= 60) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-rose-700 bg-rose-50 border-rose-200";
}

export default function Scorecard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [notesFor, setNotesFor] = useState(null); // { driver_id, driver_name }
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    api.get("/drivers/scorecard", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  const openNotes = (driver_id, driver_name) => {
    setNotesFor({ driver_id, driver_name });
    setNoteText("");
    api.get(`/drivers/${driver_id}/notes`).then((r) => setNotes(r.data));
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/drivers/${notesFor.driver_id}/notes`, { text: noteText });
      setNotes((n) => [data, ...n]);
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeNote = async (nid) => {
    if (!confirm("Delete this note?")) return;
    try {
      await api.delete(`/drivers/notes/${nid}`);
      setNotes((n) => n.filter((x) => x.id !== nid));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = data?.rows || [];
  const top = rows[0];
  const bottom = useMemo(() => {
    const active = rows.filter((r) => r.total_rides > 0);
    return active.length ? active[active.length - 1] : null;
  }, [rows]);

  return (
    <div data-testid="scorecard-page">
      <PageHeader subtitle="People" title="Driver Scorecard" />

      <div className="flex items-center gap-2 mb-5">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setDays(r)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${days === r ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            data-testid={`range-${r}`}
          >
            Last {r} days
          </button>
        ))}
      </div>

      {(top || bottom) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {top && (
            <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-4" data-testid="top-driver-card">
              <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center"><Trophy size={18} /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Top performer</div>
                <div className="font-display font-semibold text-slate-900">{top.driver_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {top.total_rides} rides · {top.completion_rate ?? "—"}% completion · {top.off_route_incidents + top.speeding_incidents} incidents
                </div>
              </div>
              <div className={`text-2xl font-display font-bold px-3 py-1 rounded-md border ${scoreColor(top.score)}`}>{top.score}</div>
            </div>
          )}
          {bottom && bottom.driver_id !== top?.driver_id && (
            <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-4" data-testid="bottom-driver-card">
              <div className="w-10 h-10 rounded-md bg-rose-50 text-rose-700 border border-rose-200 flex items-center justify-center"><TrendingDown size={18} /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Needs coaching</div>
                <div className="font-display font-semibold text-slate-900">{bottom.driver_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {bottom.total_rides} rides · {bottom.completion_rate ?? "—"}% completion · {bottom.off_route_incidents + bottom.speeding_incidents} incidents
                </div>
              </div>
              <div className={`text-2xl font-display font-bold px-3 py-1 rounded-md border ${scoreColor(bottom.score)}`}>{bottom.score}</div>
            </div>
          )}
        </div>
      )}

      <TableShell
        columns={["Driver", "Rides", "Completed", "Canceled", "Miles", "Off-route", "Speeding", "Completion", "Score", "Notes"]}
        empty={rows.length === 0 ? "No drivers or no ride data in this period." : null}
      >
        {rows.map((r) => (
          <tr key={r.driver_id} className="tbl-row border-t border-slate-100" data-testid={`score-row-${r.driver_id}`}>
            <td className="px-4 py-2.5 font-medium">{r.driver_name}
              <div className="text-slate-500 text-[11px] capitalize">{r.status?.replace("_", " ")}</div>
            </td>
            <td className="px-4 py-2.5 font-mono">{r.total_rides}</td>
            <td className="px-4 py-2.5 text-emerald-700 font-mono">{r.completed}</td>
            <td className="px-4 py-2.5 text-rose-700 font-mono">{r.canceled}</td>
            <td className="px-4 py-2.5 font-mono">{r.miles.toFixed(1)}</td>
            <td className="px-4 py-2.5">
              <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <AlertTriangle size={10} /> {r.off_route_incidents}
              </span>
            </td>
            <td className="px-4 py-2.5">
              <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                <Gauge size={10} /> {r.speeding_incidents}
              </span>
            </td>
            <td className="px-4 py-2.5 font-mono">{r.completion_rate == null ? "—" : `${r.completion_rate}%`}</td>
            <td className="px-4 py-2.5">
              <span className={`text-sm font-display font-semibold rounded-md px-2 py-0.5 border ${scoreColor(r.score)}`}>{r.score}</span>
            </td>
            <td className="px-4 py-2.5">
              <button className="text-[#005E82] hover:underline text-xs inline-flex items-center gap-1" onClick={() => openNotes(r.driver_id, r.driver_name)} data-testid={`notes-${r.driver_id}`}>
                <MessageSquarePlus size={12} /> Notes
              </button>
            </td>
          </tr>
        ))}
      </TableShell>

      <div className="mt-4 text-xs text-slate-500">
        Score = 100 − off-route (×3, capped 30) − speeding (×5, capped 30) − cancels (×2, capped 20) ± completion adjustment. Range 0–100.
      </div>

      <Drawer
        open={!!notesFor}
        onClose={() => setNotesFor(null)}
        title={`Coaching notes · ${notesFor?.driver_name || ""}`}
      >
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              placeholder="e.g. Coached on 2nd Ave route after two drift incidents this week."
              className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="note-text"
            />
            <div className="flex justify-end mt-2">
              <button onClick={addNote} disabled={!noteText.trim()} className="btn-primary text-xs disabled:opacity-60" data-testid="add-note-btn">
                Add note
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {notes.length === 0 && <div className="empty">No coaching notes yet.</div>}
            {notes.map((n) => (
              <div key={n.id} className="border-l-2 border-slate-200 pl-3 py-1" data-testid={`note-${n.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{n.author_name}</span>
                    <span className="text-slate-400"> · {new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <button onClick={() => removeNote(n.id)} className="text-slate-400 hover:text-rose-600" data-testid={`del-note-${n.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>
        </div>
      </Drawer>
    </div>
  );
}
